import * as React from 'react'
import { authService } from '@/services/AuthService'
import { tokenStorage } from '@/helpers/tokenStorage'
import logger, { toErrorMessage } from '@/helpers/logger'
import type { LoginInput, User } from '@/schemas/auth.schema'
import type { CatalogPermission } from '@lib/permissions/role-permissions'

interface AuthContextValue {
  /** The signed-in user, or `null` when signed out. */
  user: User | null
  /** The current access token, or `null` when signed out. */
  token: string | null
  /** True while the initial "restore session from token" check is in flight. */
  isLoading: boolean
  /** Whether a user is currently authenticated. */
  isAuthenticated: boolean
  /** Exchange credentials for a session; resolves once the user is loaded. */
  login: (input: LoginInput) => Promise<void>
  /** Clear the session and stored token. */
  logout: () => void
  /** Switch active company; refreshes token + user permissions. */
  switchCompany: (companyId: string) => Promise<void>
  /** True when the user is a Pixel Mortgage platform admin. */
  isPlatformAdmin: boolean
  /** Effective permission strings for the current company context. */
  permissions: CatalogPermission[]
  /** Whether the current principal holds a given `resource.action` permission. */
  hasPermission: (permission: CatalogPermission) => boolean
}

const AuthContext = React.createContext<AuthContextValue | null>(null)

interface AuthProviderProps {
  children: React.ReactNode
}

/**
 * Provides authentication state to the app. On mount it restores a session from
 * a persisted token (by fetching the current user); a failed restore clears the
 * token and drops to the signed-out state.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  // helpers → state → effects → handlers → render
  const [user, setUser] = React.useState<User | null>(null)
  const [token, setToken] = React.useState<string | null>(() => tokenStorage.get())
  const [isLoading, setIsLoading] = React.useState<boolean>(true)

  React.useEffect(() => {
    let active = true

    async function restoreSession(): Promise<void> {
      if (!tokenStorage.get()) {
        setIsLoading(false)
        return
      }
      try {
        const currentUser = await authService.me()
        if (active) setUser(currentUser)
      } catch (error) {
        logger.info('Saved session is no longer valid; signing out', {
          error: toErrorMessage(error),
        })
        tokenStorage.clear()
        if (active) {
          setUser(null)
          setToken(null)
        }
      } finally {
        if (active) setIsLoading(false)
      }
    }

    void restoreSession()
    return () => {
      active = false
    }
  }, [])

  const login = React.useCallback(async (input: LoginInput): Promise<void> => {
    const result = await authService.login(input)
    tokenStorage.set(result.accessToken)
    setToken(result.accessToken)
    setUser(result.user)
  }, [])

  const logout = React.useCallback((): void => {
    tokenStorage.clear()
    setToken(null)
    setUser(null)
  }, [])

  const switchCompany = React.useCallback(
    async (companyId: string): Promise<void> => {
      const result = await authService.switchCompany(companyId)
      tokenStorage.set(result.accessToken)
      setToken(result.accessToken)
      setUser(result.user)
    },
    [],
  )

  const hasPermission = React.useCallback(
    (permission: CatalogPermission): boolean => {
      if (!user) return false
      if (user.isPlatformAdmin) return true
      return user.permissions.includes(permission)
    },
    [user],
  )

  const value: AuthContextValue = {
    user,
    token,
    isLoading,
    isAuthenticated: user !== null,
    login,
    logout,
    switchCompany,
    isPlatformAdmin: user?.isPlatformAdmin ?? false,
    permissions: user?.permissions ?? [],
    hasPermission,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

/**
 * Access the auth context. Throws if used outside an {@link AuthProvider} so a
 * misplaced consumer fails loudly instead of reading a stale default.
 */
export function useAuth(): AuthContextValue {
  const context = React.useContext(AuthContext)
  if (context === null) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
